import React, { useState } from 'react';
import { ArrowUpRight, CheckCircle2, ChevronDown, Lightbulb, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { useSeccionPermisos } from '../../hooks/useSeccionPermisos';
import { formatoMonto } from '../compartidos/useAnalisis';
import { AnalisisCaso } from './AnalisisCaso';
import { EpisodioAccionDialog } from './EpisodioAccionDialog';
import { EpisodioHistorial } from './EpisodioHistorial';
import { EpisodioNotas } from './EpisodioNotas';
import { FlujoCircular } from './FlujoCircular';
import { MovimientosCasoTabla } from './MovimientosCasoTabla';
import {
  EPISODIO_CERRADO, ESTADO_EPISODIO, esCircular, fFechaHora, periodoEpisodio, SEVERIDAD,
} from './episodioUtils';

const SUBS = [['movimientos', 'Movimientos'], ['analisis', 'Análisis'], ['historial', 'Historial']];

const DatosMonitor = ({ datos }) => {
  const entradas = Object.entries(datos || {}).filter(([, v]) => v !== null && typeof v !== 'object');
  if (!entradas.length) return null;
  return (
    <details className="group rounded-lg border border-slate-200 bg-slate-50/60 text-xs" data-testid="episodio-datos-monitor">
      <summary className="flex cursor-pointer items-center gap-1 px-3 py-2 font-medium text-slate-600">
        <ChevronDown size={13} className="transition-transform group-open:rotate-180" /> Datos del monitor
      </summary>
      <div className="grid gap-x-6 gap-y-1 px-3 pb-3 sm:grid-cols-2">
        {entradas.slice(0, 12).map(([k, v]) => (
          <div key={k} className="flex justify-between gap-2"><span className="text-slate-500">{k.replace(/_/g, ' ')}</span>
            <span className="truncate font-medium text-slate-700">{typeof v === 'number' ? v.toLocaleString('es-AR') : String(v)}</span></div>
        ))}
      </div>
    </details>
  );
};

// G2: el episodio elegido (frase, Movimientos / Análisis / Historial, notas y acciones).
export const EpisodioDetalle = ({ caseId, episodio: e, motivos, onCambio }) => {
  const [sub, setSub] = useState('movimientos');
  const [accion, setAccion] = useState(null);
  const est = ESTADO_EPISODIO[e.estado] || ESTADO_EPISODIO.VENCIDO;
  const cerrado = EPISODIO_CERRADO.includes(e.estado);
  // Cerrar, descartar o escalar exige permiso de editar en Casos (ver el episodio alcanza con ver).
  const perm = useSeccionPermisos('gestion-de-casos', 'cases');
  const puedeEditar = !!perm?.editar;
  const bloqueado = cerrado || !puedeEditar;
  const ayuda = !puedeEditar && perm ? 'Necesitás permiso de editar en Casos' : undefined;
  return (
    <section className="space-y-4" data-testid={`episodio-detalle-${e.id}`}>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${SEVERIDAD[e.severidad] || 'bg-slate-200'}`}>{e.severidad}</span>
              <h3 className="text-base font-semibold text-slate-900" data-testid="episodio-detalle-monitor">{e.monitor}</h3>
              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${est.c}`} data-testid="episodio-detalle-estado">{est.label}</span>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              <span className="font-mono">{periodoEpisodio(e)}</span> · {e.cantidad} movimientos · <span className="font-mono">{formatoMonto(e.monto)}</span>
            </p>
          </div>
          <div className="flex flex-wrap gap-2" title={ayuda} data-testid="episodio-acciones">
            <Button size="sm" variant="outline" disabled={bloqueado} onClick={() => setAccion('DESCARTAR')} data-testid="episodio-descartar"
              className="h-8 gap-1 border-zinc-300 text-zinc-700 hover:bg-zinc-50"><XCircle size={14} /> Descartar</Button>
            <Button size="sm" variant="outline" disabled={bloqueado} onClick={() => setAccion('REVISADO')} data-testid="episodio-revisado"
              className="h-8 gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"><CheckCircle2 size={14} /> Revisado</Button>
            <Button size="sm" variant="outline" disabled={bloqueado} onClick={() => setAccion('ESCALAR')} data-testid="episodio-escalar"
              className="h-8 gap-1 border-orange-300 text-orange-700 hover:bg-orange-50"><ArrowUpRight size={14} /> Escalar</Button>
          </div>
        </div>
        <p className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="episodio-frase">
          <Lightbulb size={15} className="mt-0.5 shrink-0 text-amber-500" /> {e.frase}
        </p>
        {cerrado && (e.conclusion || e.motivo_descarte) && (
          <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-700" data-testid="episodio-conclusion-guardada">
            {e.motivo_descarte && <span className="mr-1 font-semibold">{motivos[e.motivo_descarte] || e.motivo_descarte}:</span>}
            {e.conclusion}
            {e.cerrado_por && <span className="ml-1 text-slate-400">— {e.cerrado_por}, {fFechaHora(e.fecha_cierre_episodio)}</span>}
          </p>
        )}
        <div className="mt-3"><DatosMonitor datos={e.datos_adicionales} /></div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex gap-1 border-b border-slate-100 px-3 pt-2">
          {SUBS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setSub(k)} data-testid={`episodio-sub-${k}`}
              className={`rounded-t-md border-b-2 px-3 py-2 text-sm transition-colors ${sub === k ? 'border-sky-600 font-semibold text-sky-800' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>{l}</button>
          ))}
        </div>
        <div className="p-4">
          {sub === 'movimientos' && (
            <MovimientosCasoTabla caseId={caseId} episodioId={e.id}
              renderExtra={esCircular(e) ? (filas) => <FlujoCircular filas={filas} /> : undefined} />
          )}
          {sub === 'analisis' && <AnalisisCaso casoId={caseId} episodioId={e.id} />}
          {sub === 'historial' && <EpisodioHistorial caseId={caseId} episodioId={e.id} />}
        </div>
      </div>

      <EpisodioNotas caseId={caseId} episodio={e} onCambio={onCambio} />
      <EpisodioAccionDialog caseId={caseId} episodio={e} accion={accion} motivos={motivos} onClose={() => setAccion(null)} onCambio={onCambio} />
    </section>
  );
};

export default EpisodioDetalle;
